import { request } from "@/utilities/libs/request.ts";
import {
  IRequestArgs,
  IResponseData,
  ListResponseData,
} from "@/utilities/types/requests.ts";
import { ERequestMethods } from "@/utilities/enums/RequestMethod.ts";
import { ROUNDROBIN } from "../enums/RoundRobin";
import {
  IQueryRoundRobinUser,
  IRoundRobinUser,
  ISchedule,
  IScheduleDTO,
  ITimeSlotConfig,
  ITimeSlotConfigDTO,
  IQueueState,
  UpdateResult,
} from "../types/RoundRobin";

function getTimeSlot({ queryParams }: IRequestArgs) {
  const query = {
    filter: queryParams?.query?.filter?.at(0) || {},
    ...queryParams?.pagination,
  };

  return request<ListResponseData<ISchedule>>({
    method: ERequestMethods.GET,
    url: ROUNDROBIN.GET_TIME_SLOT,
    params: query,
  });
}

function getQueue() {
  return request<IResponseData<IQueueState>>({
    method: ERequestMethods.GET,
    url: ROUNDROBIN.QUEUE,
  });
}

function distributedHistory(
  queryParams: IRequestArgs
): Promise<ListResponseData<ISchedule>> {
  if (!queryParams.queryParams?.query?.filter[0].id) {
    throw new Error("An 'id' parameter is required.");
  }

  // Construct the query object, ensuring nullish values are handled gracefully
  const query = {
    sort: {
      createdAt: "DESC",
    },
    ...queryParams?.queryParams?.pagination,
  };

  return request<ListResponseData<ISchedule>>({
    method: ERequestMethods.GET,
    url: ROUNDROBIN.DISTRIBUTED_HISTORY,
    params: query,
  });
}

function addTimeSlot(data: IScheduleDTO) {
  return request<IResponseData<IScheduleDTO>>({
    method: ERequestMethods.POST,
    url: ROUNDROBIN.ADĐ_TIME_SLOT,
    data,
  });
}

function updateRoundRobinStatus(id: string, data: Partial<any>) {
  return request<IResponseData<IScheduleDTO>>({
    method: ERequestMethods.PATCH,
    url: ROUNDROBIN.AGENT_STATUS(id),
    data,
  });
}

function updateAgentsInTimeSlot(id: string, data: Partial<string[]>) {
  return request<IResponseData<IScheduleDTO>>({
    method: ERequestMethods.PATCH,
    url: ROUNDROBIN.UPDATE_AGENTS(id),
    data,
  });
}

function removeTimeSlot(id: string) {
  return request<IResponseData<IScheduleDTO>>({
    method: ERequestMethods.DELETE,
    url: ROUNDROBIN.REMOVE(id),
  });
}

function getTimeSlotConfig() {
  return request<IResponseData<ITimeSlotConfig>>({
    method: ERequestMethods.GET,
    url: ROUNDROBIN.TIME_SLOT_CONFIG,
  });
}

function updateTimeSlotConfig({ data }: { data: Partial<ITimeSlotConfigDTO> }) {
  return request<IResponseData<ITimeSlotConfig>>({
    method: ERequestMethods.PUT,
    url: ROUNDROBIN.TIME_SLOT_CONFIG,
    data,
  });
}

function deleteUserSubAccount({ data }: { data: { subUserId: string } }) {
  return request<IResponseData<UpdateResult>>({
    method: ERequestMethods.DELETE,
    url: ROUNDROBIN.USER_SUB_ACCOUNT,
    data,
  });
}

function getUser(params: IQueryRoundRobinUser) {
  return request<IResponseData<IRoundRobinUser>>({
    method: ERequestMethods.GET,
    url: ROUNDROBIN.GET_USER,
    params,
  });
}

function createUserSubAccount({ data }: { data: { subUserId: string } }) {
  return request<IResponseData<IRoundRobinUser>>({
    method: ERequestMethods.POST,
    url: ROUNDROBIN.USER_SUB_ACCOUNT,
    data,
  });
}

const RoundRobinRepository = Object.freeze({
  getTimeSlot,
  getQueue,
  addTimeSlot,
  distributedHistory,
  updateAgentsInTimeSlot,
  updateRoundRobinStatus,
  removeTimeSlot,
  getTimeSlotConfig,
  updateTimeSlotConfig,
  deleteUserSubAccount,
  getUser,
  createUserSubAccount,
});

export default RoundRobinRepository;
